import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';

import { LAYER } from '@doltech/utils/lib/constants';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { LandingBlogButtonReadingTableOfContent } from '../LandingBlogButtonReadingTableOfContent/LandingBlogButtonReadingTableOfContent';

const PopoverContent = styled.div`
  position: relative;
  z-index: ${LAYER.LAYER3};
`;

const PopoverContainer = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  opacity: 0;
  z-index: -1;
  pointer-events: none;
  transition: all 0.1s ease-in-out;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(36, 41, 56, 0.45);
  }

  .floating-popover-content {
    transform: translateY(100%);
    transition: transform 0.2s ease-in-out;
  }

  &.visible {
    opacity: 1;
    z-index: ${LAYER.LAYER3};
    pointer-events: auto;

    .floating-popover-content {
      transform: translateY(0);
    }
  }

  ${fromScreen(451)} {
    position: absolute;
    bottom: 52px;
    right: 0;
    top: auto;
    left: auto;

    min-width: 320px;

    &:before {
      display: none;
    }
  }
`;

const Container = styled.div`
  position: relative;
`;

interface PopoverProps {
  togglePopoverVisible: () => void;
  setPopoverVisible: (visible: boolean) => void;
}

interface LandingBlogFloatingActionTocPopoverLayoutProps {
  pageUrl?: string;

  renderInfoPopover?: (popoverProps: PopoverProps) => React.ReactNode;
  renderTocPopover?: (popoverProps: PopoverProps) => React.ReactNode;

  onClickToTop?: () => void;

  canViewToc?: boolean;
}

export const LandingBlogFloatingActionTocPopoverLayout = (props: LandingBlogFloatingActionTocPopoverLayoutProps) => {
  const { pageUrl, renderInfoPopover, renderTocPopover, onClickToTop, canViewToc = false } = props;
  const [popoverType, setPopoverType] = React.useState(null);

  return (
    <Container className="floating-action-toc-popover-layout">
      <LandingBlogButtonReadingTableOfContent
        pageUrl={pageUrl}
        onClickInfo={() => setPopoverType((prevPopoverType) => {
          if (prevPopoverType === 'INFO') {
            return null;
          }
          return 'INFO';
        })}
        onClickToc={() => setPopoverType((prevPopoverType) => {
          if (prevPopoverType === 'TOC') {
            return null;
          }
          return 'TOC';
        })}
        onClickToTop={() => {
          setPopoverType(null);
          onClickToTop?.();
        }}
        infoActive={popoverType === 'INFO'}
        tocActive={popoverType === 'TOC'}
        canViewToc={canViewToc}
      />
      <PopoverContainer className={cl('popover-info', { visible: popoverType === 'INFO' })}>
        <PopoverContent className="floating-popover-content">
          {renderInfoPopover?.({
            togglePopoverVisible: () => setPopoverType((prevType) => prevType === 'INFO' ? null : 'INFO'),
            setPopoverVisible: (popoverVisible) => setPopoverType(popoverVisible ? 'INFO' : null),
          })}
        </PopoverContent>
      </PopoverContainer>
      <PopoverContainer className={cl('popover-toc', { visible: popoverType === 'TOC' })}>
        <PopoverContent className="floating-popover-content">
          {renderTocPopover?.({
            togglePopoverVisible: () => setPopoverType((prevType) => prevType === 'INFO' ? null : 'INFO'),
            setPopoverVisible: (popoverVisible) => setPopoverType(popoverVisible ? 'TOC' : null),
          })}
        </PopoverContent>
      </PopoverContainer>
    </Container>
  );
};
